import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import * as _ from "lodash";
import { EditActions, editActions } from "../../actions/editActions";
import { hasWriteAccess, nameofFactory } from "../../utils/utils";
import Editor from "../../components/Editing/Editor";
import NumberField from "../../components/Editing/NumberField";
import TextField from "../../components/Editing/TextField";
import { Divider, Modal } from "semantic-ui-react";
import DropdownField from "../../components/Editing/DropdownField";
import { getDriveSignalGroupNames } from "../../utils/pdApiUtils";
import CheckboxField from "../../components/Editing/CheckboxField";
import ButtonField from "../../components/Editing/ButtonField";
import { EditLocalizationStatus } from "./EditLocalizationStatus";
import TextIndicatorField from "../../components/Editing/TextIndicatorField";

type StateProps = {
  editedDriveSignal: Api.DriveSignal;
  storedDriveSignal: Api.DriveSignal;
  pdApi: State.PdApi;
  activeUser?: Api.User;
  columns: State.ColumnMap;
  isShortenTextEnabled: boolean;
};

type DispatchProps = {
  editActions: typeof editActions;
};

type OwnProps = {
  toggleModal: () => void;
};

export type EditDriveSignalProps = StateProps & DispatchProps & OwnProps;

export type EditDriveSignalState = {
  isEditLocalizationStatusOpen: boolean;
  locsOriginal: Api.LocsDriveSignal[];
  locsEdited: Api.LocsDriveSignal[];
};

const driveSignalFields = nameofFactory<Api.DriveSignal>();

export class EditDriveSignal extends React.Component<EditDriveSignalProps, EditDriveSignalState> {
  constructor(props: EditDriveSignalProps) {
    super(props);
    this.state = {
      isEditLocalizationStatusOpen: false,
      locsOriginal: [],
      locsEdited: []
    };
  }
  public render(): JSX.Element {
    const { isEditLocalizationStatusOpen } = this.state;
    const {
      editedDriveSignal,
      storedDriveSignal,
      activeUser,
      columns,
      pdApi,
      toggleModal,
      isShortenTextEnabled
    } = this.props;

    const isNew = !storedDriveSignal;
    const driveSignalGroupOptions = getDriveSignalGroupNames(pdApi);
    const permissions = hasWriteAccess(activeUser, "Drive");

    return (
      <Editor
        itemName={"drive signal"}
        originalObject={storedDriveSignal}
        editedObject={editedDriveSignal}
        hasWritePermission={hasWriteAccess(activeUser, "Drive")}
        isValid={this.isAllFieldsValid()}
        cancel={this.props.editActions.cancelEditing}
        update={() => {
          this.props.editActions.setEdited({
            ...editedDriveSignal,
            locs: this.state.locsEdited
          });
          this.props.editActions.update("signals");
        }}
        create={() => {
          this.props.editActions.setEdited({
            ...editedDriveSignal,
            locs: this.state.locsEdited
          });
          this.props.editActions.add("signals");
        }}
        copy={() => {
          this.props.editActions.setEdited({
            ...editedDriveSignal,
            locs: this.state.locsEdited
          });
          this.props.editActions.startEditing(editedDriveSignal, true);
        }}
        remove={{ callback: toggleModal }}
      >
        <NumberField
          id="code"
          label="Code"
          value={editedDriveSignal.code}
          isError={!this.isFieldValid(driveSignalFields("code"))}
          errorMessage="The code must be unique."
          readonly={!isNew}
          onChange={code => this.props.editActions.setEdited({ ...editedDriveSignal, code })}
        />
        <DropdownField
          id="driveSignalGroup"
          label="Signal Group"
          value={editedDriveSignal.signalGroupEnumValue}
          options={driveSignalGroupOptions}
          search={true}
          onChange={driveSignalGroupGroupIndex => {
            const selected = pdApi.signalGroups.find(
              i => i.enumValue === driveSignalGroupGroupIndex
            );
            if (selected) {
              this.props.editActions.setEdited({
                ...editedDriveSignal,
                signalGroupEnumValue: selected.enumValue
              });
            }
          }}
          disabled={!permissions}
        />
        <TextField
          id="swName"
          label="Sw Name"
          value={editedDriveSignal.swName}
          isError={!this.isFieldValid(driveSignalFields("swName"))}
          errorMessage={
            !this.isEmptyString(editedDriveSignal.swName) ? "The Sw name must be unique." : ""
          }
          onChange={swName =>
            this.props.editActions.setEdited({ ...editedDriveSignal, swName: swName.trim() })
          }
          readonly={!permissions}
        />
        <Divider horizontal={true}>{"English translations"}</Divider>
        <TextIndicatorField
          id={"needsUpdateIndicator"}
          text={"Edited texts will be translated."}
          isWarning={this.englishTranslationsChanged()}
        />
        <TextField
          id="name"
          label="English Name"
          value={editedDriveSignal.name}
          isError={!this.isFieldValid(driveSignalFields("name"))}
          onChange={value => {
            this.props.editActions.setEdited({
              ...editedDriveSignal,
              name: value.trim()
            } as Api.DriveSignal);
          }}
          readonly={!permissions}
        />
        <TextField
          id="description"
          label="Description"
          value={editedDriveSignal.description}
          isError={!this.isFieldValid(driveSignalFields("description"))}
          onChange={value => {
            this.props.editActions.setEdited({
              ...editedDriveSignal,
              description: value.trim()
            } as Api.DriveSignal);
          }}
          readonly={!permissions}
        />
        <ButtonField
          id={"viewTranslationsButton"}
          label="View translations"
          onButtonClick={this.toggleEditLocalizationStatusModal}
        />
        <Divider horizontal={true}>{"Signal properties"}</Divider>
        <TextField
          id="unitName"
          label="Unit Name"
          value={editedDriveSignal.unitName}
          isError={!this.isFieldValid(driveSignalFields("unitName"))}
          onChange={unitName =>
            this.props.editActions.setEdited({ ...editedDriveSignal, unitName: unitName.trim() })
          }
          readonly={!permissions}
        />
        <CheckboxField
          id={"localizable"}
          label={"Signal is localizable"}
          checked={editedDriveSignal.localizable}
          onChange={value =>
            this.props.editActions.setEdited({ ...editedDriveSignal, localizable: value })
          }
          disabled={!permissions}
        />
        <Modal size="large" open={isEditLocalizationStatusOpen} className="data-sections-modal">
          <EditLocalizationStatus
            itemType={"Drive Signal"}
            editedItem={{ ...editedDriveSignal, locs: this.state.locsEdited }}
            getOption="signalLocalizations"
            columns={columns["Drive Signal Localization Status"]}
            pdApi={pdApi}
            toggleEditLocalizationStatusModal={this.toggleEditLocalizationStatusModal}
            setLocalizationsEdited={this.setLocalizationsEdited}
            isShortenTextEnabled={isShortenTextEnabled}
            englishTranslationChanged={this.englishTranslationsChanged()}
            userPermissions={permissions}
            locsOriginal={this.state.locsOriginal || []}
            setLocalizationsNonEdited={this.setLocalizationsNonEdited}
          />
        </Modal>
      </Editor>
    );
  }

  private englishTranslationsChanged = (): boolean =>
    this.props.editedDriveSignal.name !== this.props.storedDriveSignal?.name ||
    this.props.editedDriveSignal.description !== this.props.storedDriveSignal?.description;

  private handleEnglishTranslationsChange(item: Api.DriveSignal) {
    const { storedDriveSignal, editedDriveSignal } = this.props;
    const { locsOriginal, locsEdited } = this.state;

    // create both edited and stored item locs if needed
    const edited =
      locsEdited.length === 0
        ? [
            {
              text: item.name,
              description: item.description,
              localeNavigation: { locale: "en-US" }
            } as Api.LocsDriveSignal
          ]
        : this.state.locsEdited.map(loc => {
            if (loc.localeNavigation.locale === "en-US") {
              return {
                ...loc,
                text: item.name,
                description: item.description
              } as Api.LocsDriveSignal;
            } else {
              return loc;
            }
          });
    const original =
      locsOriginal.length === 0
        ? [
            {
              text: storedDriveSignal?.name,
              description: storedDriveSignal?.description,
              localeNavigation: { locale: "en-US" }
            } as Api.LocsDriveSignal
          ]
        : locsOriginal;

    this.setState((prevState: EditDriveSignalState) => ({
      ...prevState,
      locsEdited: edited,
      locsOriginal: original
    }));
    this.props.editActions.setEdited({
      ...editedDriveSignal,
      name: item.name,
      description: item.description
    });
  }

  private toggleEditLocalizationStatusModal = (): void => {
    this.setState((prevState: EditDriveSignalState) => ({
      ...prevState,
      isEditLocalizationStatusOpen: !prevState.isEditLocalizationStatusOpen
    }));
  };

  private setLocalizationsEdited = (
    newLocalizations: Api.LocsDriveSignal[],
    oldLocalizations: Api.LocsDriveSignal[]
  ): void => {
    const { editedDriveSignal } = this.props;
    this.setState((prevState: EditDriveSignalState) => ({
      ...prevState,
      locsEdited: newLocalizations,
      locsOriginal: oldLocalizations
    }));
    this.props.editActions.setEdited({
      ...editedDriveSignal,
      name:
        newLocalizations.find(loc => loc.localeNavigation.locale === "en-US")?.text ||
        editedDriveSignal?.name,
      description:
        newLocalizations.find(loc => loc.localeNavigation.locale === "en-US")?.description ||
        editedDriveSignal?.description,
      locs: newLocalizations
    } as Api.DriveSignal);
  };

  private setLocalizationsNonEdited = (Localizations: Api.LocsDriveSignal[]): void => {
    this.setState((prevState: EditDriveSignalState) => ({
      ...prevState,
      locsOriginal: Localizations
    }));
  };
  private isFieldValid = (field: string): boolean => {
    const { editedDriveSignal, storedDriveSignal, pdApi } = this.props;
    if (!Number.isInteger(editedDriveSignal.code)) return false;
    const isNew = !storedDriveSignal;
    switch (field) {
      case driveSignalFields("code"):
        return _.values(pdApi.signals).find(
          driveSignal =>
            (isNew || driveSignal.id !== editedDriveSignal.id) &&
            driveSignal.code === editedDriveSignal.code
        )
          ? false
          : true;
      case driveSignalFields("swName"): {
        const { swName } = editedDriveSignal;
        if (this.isEmptyString(swName)) {
          return false;
        } else {
          return _.values(pdApi.signals).find(
            driveSignal =>
              (isNew || driveSignal.id !== editedDriveSignal.id) &&
              driveSignal.swName?.trim().toLowerCase() ===
                editedDriveSignal.swName?.trim().toLowerCase()
          )
            ? false
            : true;
        }
      }
      case driveSignalFields("name"): {
        const { name } = editedDriveSignal;
        return !this.isEmptyString(name);
      }
      case driveSignalFields("description"): {
        const { description } = editedDriveSignal;
        return !this.isEmptyString(description);
      }
      default:
        return true;
    }
  };

  private isEmptyString = (str: string): boolean => !(!!str.trim() && str.trim().length > 0);

  private isAllFieldsValid = (): boolean => {
    const { editedDriveSignal } = this.props;
    const temp = _.keys(editedDriveSignal);
    for (const key of temp) {
      if (!this.isFieldValid(key)) {
        return false;
      }
    }
    return true;
  };
}

const mapStateToProps = (state: State.Root) => ({
  editedDriveSignal: state.editView.editedObject,
  storedDriveSignal: state.editView.storedObject,
  pdApi: state.pdApi,
  activeUser: state.main.activeUser,
  isShortenTextEnabled: state.main.isShortenTextEnabled
});

const mapDispatchToProps = (dispatch: Dispatch<EditActions>) => ({
  editActions: bindActionCreators({ ...editActions }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(EditDriveSignal);


